import React, { useState, useRef, useEffect, useCallback } from 'react';
import { GoogleMap, Marker, DirectionsRenderer } from '@react-google-maps/api';
import { FaMapMarkerAlt, FaSearch, FaTimes, FaSpinner, FaPlus, FaRoute } from 'react-icons/fa';

const defaultCenter = { lat: -28.4793, lng: 24.6729 };
const mapContainerStyle = { height: '350px', width: '100%' };

// 🌍 SADC countries (auto cross-border detection needs non-SA picks too)
const SADC_COUNTRIES = ['za', 'zw', 'mz', 'bw', 'na', 'ls', 'sz', 'zm', 'mw', 'ao', 'tz', 'cd', 'mg', 'mu', 'sc', 'bi', 'rw'];
const extractCountry = (result) => {
  if (!result || !result.address_components) return null;
  const comp = result.address_components.find(c => c.types && c.types.includes('country'));
  return comp ? (comp.long_name || comp.short_name || null) : null;
};

// ============================================
// BUILD THE ROUTE PAYLOAD FOR STORAGE
// ============================================
// The route drawn here used to be THROWN AWAY — only distance/duration was reported — which is
// why `quote_routes.route_optimized` was NULL on all 59 rows and the load pages had nothing to
// draw. This packages the whole Directions result so it can be stored once and redrawn exactly
// as quoted, forever.
// Module scope on purpose — see PROJECT-NOTES §0 RULES (never define inside a component).
const buildRoutePayload = (result) => {
  const route = result && result.routes && result.routes[0];
  if (!route) return null;

  const encodePolyline = (poly) => {
    if (!poly) return null;
    if (typeof poly.getEncodedPath === 'function') return poly.getEncodedPath();
    return poly.points || null;
  };

  const legs = (route.legs || []).map((l) => ({
    from: l.start_address || null,
    to: l.end_address || null,
    distance_km: Math.round(((l.distance && l.distance.value) || 0) / 100) / 10,
    duration_min: Math.round(((l.duration && l.duration.value) || 0) / 60),
    start: l.start_location ? { lat: l.start_location.lat(), lng: l.start_location.lng() } : null,
    end: l.end_location ? { lat: l.end_location.lat(), lng: l.end_location.lng() } : null
  }));

  const totalMin = legs.reduce((s, l) => s + l.duration_min, 0);
  const totalKm = Math.round(legs.reduce((s, l) => s + l.distance_km, 0) * 10) / 10;
  const first = legs[0] || null;
  const last = legs[legs.length - 1] || null;
  const b = route.bounds;

  return {
    version: 1,
    captured_at: new Date().toISOString(),
    origin: first ? { address: first.from, lat: first.start && first.start.lat, lng: first.start && first.start.lng } : null,
    destination: last ? { address: last.to, lat: last.end && last.end.lat, lng: last.end && last.end.lng } : null,
    // Any intermediate stopovers the user added (between first and last leg).
    stops: legs.slice(1, -1).map((l) => ({ address: l.from, lat: l.start && l.start.lat, lng: l.start && l.start.lng })),
    distance_km: totalKm,
    duration_min: totalMin,
    duration_text: totalMin ? `${Math.floor(totalMin / 60)}h ${totalMin % 60}m` : null,
    polyline: encodePolyline(route.overview_polyline),
    bounds: b ? {
      north: b.getNorthEast().lat(),
      east: b.getNorthEast().lng(),
      south: b.getSouthWest().lat(),
      west: b.getSouthWest().lng()
    } : null,
    legs
  };
};

function InteractiveRouteMap({
  originValue = '',
  destinationValue = '',
  onOriginChange,
  onDestinationChange,
  onDistanceCalculated,
  onOriginCoordsChange,
  onDestCoordsChange,
  onRouteChange,
  initialOriginCoords = null,
  initialDestCoords = null
}) {
  const [map, setMap] = useState(null);
  const [directions, setDirections] = useState(null);
  const mapClickHandlerRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [distance, setDistance] = useState(null);
  const [duration, setDuration] = useState(null);
  const [waypoints, setWaypoints] = useState([]);
  
  const [originInput, setOriginInput] = useState(originValue || '');
  const [originSuggestions, setOriginSuggestions] = useState([]);
  const [showOriginSuggestions, setShowOriginSuggestions] = useState(false);
  const [originCoords, setOriginCoords] = useState(initialOriginCoords);
  
  const [destInput, setDestInput] = useState(destinationValue || '');
  const [destSuggestions, setDestSuggestions] = useState([]);
  const [showDestSuggestions, setShowDestSuggestions] = useState(false);
  const [destCoords, setDestCoords] = useState(initialDestCoords);

  useEffect(() => {
    if (initialOriginCoords && initialOriginCoords.lat) setOriginCoords(initialOriginCoords);
  }, [initialOriginCoords?.lat, initialOriginCoords?.lng]);
  
  useEffect(() => {
    if (initialDestCoords && initialDestCoords.lat) setDestCoords(initialDestCoords);
  }, [initialDestCoords?.lat, initialDestCoords?.lng]);

  useEffect(() => { setOriginInput(originValue || ''); }, [originValue]);
  useEffect(() => { setDestInput(destinationValue || ''); }, [destinationValue]);

  // Auto-calculate route AND fit bounds when both coordinates are set
  useEffect(() => {
    if (originCoords && destCoords) {
      if (map) {
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend({ lat: originCoords.lat, lng: originCoords.lng });
        bounds.extend({ lat: destCoords.lat, lng: destCoords.lng });
        map.fitBounds(bounds);
      }
      calculateAndDisplayRoute();
    } else {
      setDirections(null);
      setDistance(null);
      setDuration(null);
      if (onRouteChange) onRouteChange(null);
    }
  }, [originCoords, destCoords]);

  const searchPlaces = (query, setSuggestions, setShow) => {
    if (!query || query.length < 2) { setSuggestions([]); setShow(false); return; }
    try {
      // Use Places Autocomplete for business name + address search
      const autocomplete = new window.google.maps.places.AutocompleteService();
      autocomplete.getPlacePredictions(
        { input: query, componentRestrictions: { country: SADC_COUNTRIES } },
        (predictions, status) => {
          if (status === 'OK' && predictions) {
            const formatted = predictions.map(p => ({
              place_id: p.place_id,
              description: p.description,
              // We'll get geometry when user selects via PlacesService
            }));
            setSuggestions(formatted);
            setShow(formatted.length > 0);
          } else {
            // Fallback to Geocoder if Places fails
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ address: query }, (results, status) => {
              if (status === 'OK' && results) {
                const formatted = results.map(r => ({ place_id: r.place_id, description: r.formatted_address, geometry: r.geometry }));
                setSuggestions(formatted);
                setShow(formatted.length > 0);
              } else { setSuggestions([]); setShow(false); }
            });
          }
        }
      );
    } catch (e) { console.error('Search error:', e); }
  };

  const getPlaceDetails = (placeId) => {
    return new Promise((resolve) => {
      const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
      placesService.getDetails({ placeId, fields: ['geometry', 'formatted_address', 'name', 'address_components'] }, (result, status) => {
        if (status === 'OK' && result && result.geometry) {
          resolve(result);
        } else {
          resolve(null);
        }
      });
    });
  };

  const handleSelectSuggestion = async (suggestion, isOrigin) => {
    const address = suggestion.description;
    let lat, lng;
    
    // If we have geometry from Places details, use it
    if (suggestion.geometry) {
      lat = suggestion.geometry.location.lat();
      lng = suggestion.geometry.location.lng();
    } else {
      // Fetch place details to get coordinates
      const placeDetails = await getPlaceDetails(suggestion.place_id);
      if (placeDetails && placeDetails.geometry) {
        lat = placeDetails.geometry.location.lat();
        lng = placeDetails.geometry.location.lng();
      } else {
        // Fallback to geocoding
        try {
          const geocoder = new window.google.maps.Geocoder();
          const results = await new Promise((resolve) => {
            geocoder.geocode({ address }, (results, status) => {
              if (status === 'OK' && results) resolve(results);
              else resolve(null);
            });
          });
          if (results && results.length > 0) {
            lat = results[0].geometry.location.lat();
            lng = results[0].geometry.location.lng();
          }
        } catch (e) { /* fallback failed */ }
      }
    }
    
    if (lat && lng) {
      let country = null;
      if (suggestion.geometry) {
        const geoCoder = new window.google.maps.Geocoder();
        const gres = await new Promise((resolve) => geoCoder.geocode({ location: { lat, lng } }, (res, st) => { if (st === 'OK' && res) resolve(res); else resolve(null); }));
        if (gres && gres[0]) country = extractCountry(gres[0]);
      }
      const coords = { lat, lng, address, country };
      if (isOrigin) {
        setOriginInput(address); setOriginCoords(coords); setShowOriginSuggestions(false);
        if (onOriginChange) onOriginChange(address);
        if (onOriginCoordsChange) onOriginCoordsChange(coords);
      } else {
        setDestInput(address); setDestCoords(coords); setShowDestSuggestions(false);
        if (onDestinationChange) onDestinationChange(address);
        if (onDestCoordsChange) onDestCoordsChange(coords);
      }
    }
  };

  const handleInputKeyDown = async (e, isOrigin) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const query = e.target.value;
      try {
        // Try Places first for business names
        const autocomplete = new window.google.maps.places.AutocompleteService();
        autocomplete.getPlacePredictions(
          { input: query, componentRestrictions: { country: SADC_COUNTRIES } },
          async (predictions, status) => {
            if (status === 'OK' && predictions && predictions.length > 0) {
              // Get details of first result
              const placeDetails = await getPlaceDetails(predictions[0].place_id);
              if (placeDetails && placeDetails.geometry) {
                const address = placeDetails.formatted_address || predictions[0].description;
                const lat = placeDetails.geometry.location.lat();
                const lng = placeDetails.geometry.location.lng();
                const coords = { lat, lng, address, country: extractCountry(placeDetails) };
                if (isOrigin) {
                  setOriginInput(address); setOriginCoords(coords);
                  if (onOriginChange) onOriginChange(address);
                  if (onOriginCoordsChange) onOriginCoordsChange(coords);
                } else {
                  setDestInput(address); setDestCoords(coords);
                  if (onDestinationChange) onDestinationChange(address);
                  if (onDestCoordsChange) onDestCoordsChange(coords);
                }
                return;
              }
            }
            // Fallback to Geocoder
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ address: query }, (results, status) => {
              if (status === 'OK' && results && results.length > 0) {
                const r = results[0];
                const lat = r.geometry.location.lat();
                const lng = r.geometry.location.lng();
                const coords = { lat, lng, address: r.formatted_address, country: extractCountry(r) };
                if (isOrigin) {
                  setOriginInput(r.formatted_address); setOriginCoords(coords);
                  if (onOriginChange) onOriginChange(r.formatted_address);
                  if (onOriginCoordsChange) onOriginCoordsChange(coords);
                } else {
                  setDestInput(r.formatted_address); setDestCoords(coords);
                  if (onDestinationChange) onDestinationChange(r.formatted_address);
                  if (onDestCoordsChange) onDestCoordsChange(coords);
                }
              }
            });
          }
        );
      } catch (e) { console.error('Search error:', e); }
    }
  };

  const geocodeAddress = (address) => {
    return new Promise((resolve) => {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results && results.length > 0) resolve(results[0].geometry.location);
        else resolve(null);
      });
    });
  };

  const calculateAndDisplayRoute = useCallback(async () => {
    if (!originCoords || !destCoords) return;
    setLoading(true);
    setError(null);
    try {
      const directionsService = new window.google.maps.DirectionsService();
      const originLoc = new window.google.maps.LatLng(originCoords.lat, originCoords.lng);
      const destLoc = new window.google.maps.LatLng(destCoords.lat, destCoords.lng);
      const waypointObjs = waypoints.map(wp => ({ location: new window.google.maps.LatLng(wp.lat, wp.lng), stopover: true }));
      const request = { origin: originLoc, destination: destLoc, waypoints: waypointObjs, travelMode: window.google.maps.TravelMode.DRIVING, provideRouteAlternatives: false };
      directionsService.route(request, (result, status) => {
        if (status === 'OK' && result && result.routes && result.routes.length > 0) {
          setDirections(result);
          // >>> ROUTE CAPTURE: hand the drawn route to the parent so it can be STORED. <<<
          if (onRouteChange) onRouteChange(buildRoutePayload(result));
          const route = result.routes[0];
          const leg = route.legs[0];
          const distKm = leg.distance.value / 1000;
          const durMin = leg.duration.value / 60;
          setDistance(distKm);
          setDuration(durMin);
          if (onDistanceCalculated) {
            onDistanceCalculated({ distance_km: Math.round(distKm * 10) / 10, duration_min: Math.round(durMin), distance_text: leg.distance.text, duration_text: leg.duration.text });
          }
        } else if (status === 'ZERO_RESULTS') setError('No route found between these locations');
        else setError(`Route calculation failed: ${status}`);
        setLoading(false);
      });
    } catch (err) { console.error('Route calculation error:', err); setError('Failed to calculate route'); setLoading(false); }
  }, [originCoords, destCoords, waypoints]);

  const clearOrigin = () => {
    setOriginInput(''); setOriginCoords(null); setDirections(null); setDistance(null); setDuration(null);
    if (onOriginChange) onOriginChange('');
    if (onOriginCoordsChange) onOriginCoordsChange(null);
  };

  const clearDest = () => {
    setDestInput(''); setDestCoords(null); setDirections(null); setDistance(null); setDuration(null);
    if (onDestinationChange) onDestinationChange('');
    if (onDestCoordsChange) onDestCoordsChange(null);
  };

  const addWaypoint = () => setWaypoints([...waypoints, { lat: null, lng: null, address: '' }]);
  const removeWaypoint = (index) => setWaypoints(waypoints.filter((_, i) => i !== index));

  const updateWaypoint = async (index, address) => {
    const updated = [...waypoints];
    updated[index] = { ...updated[index], address };
    setWaypoints(updated);
    // Try Places first for business names
    const autocomplete = new window.google.maps.places.AutocompleteService();
    autocomplete.getPlacePredictions({ input: address }, async (predictions, status) => {
      if (status === 'OK' && predictions && predictions.length > 0) {
        const placeDetails = await getPlaceDetails(predictions[0].place_id);
        if (placeDetails && placeDetails.geometry) {
          updated[index] = { ...updated[index], lat: placeDetails.geometry.location.lat(), lng: placeDetails.geometry.location.lng() };
          setWaypoints([...updated]);
          return;
        }
      }
      // Fallback to geocoding
      const loc = await geocodeAddress(address);
      if (loc) { updated[index] = { ...updated[index], lat: loc.lat(), lng: loc.lng() }; setWaypoints([...updated]); }
    });
  };

  const inputStyle = "w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green pl-10 text-sm";
  const suggestionBtnStyle = "w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors border-b last:border-b-0";

  return (
    <div className="space-y-3">
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-1">Origin / Pickup Location <span className="text-red-500">*</span></label>
        <div className="relative">
          <input type="text" value={originInput} onChange={(e) => { const val = e.target.value; setOriginInput(val); if (onOriginChange) onOriginChange(val); searchPlaces(val, setOriginSuggestions, setShowOriginSuggestions); if (!val) { setOriginCoords(null); setDirections(null); } }} onKeyDown={(e) => handleInputKeyDown(e, true)} onFocus={() => originSuggestions.length > 0 && setShowOriginSuggestions(true)} onBlur={() => setTimeout(() => setShowOriginSuggestions(false), 200)} placeholder="Search for pickup location..." className={inputStyle} style={{ borderColor: '#E8ECEF' }} />
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          {originInput && (<button type="button" onClick={clearOrigin} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><FaTimes size={14} /></button>)}
        </div>
        {showOriginSuggestions && originSuggestions.length > 0 && (
          <div className="absolute z-20 w-full bg-white rounded-lg shadow-lg border mt-1 max-h-48 overflow-y-auto" style={{ borderColor: '#E8ECEF' }}>
            {originSuggestions.map(s => (<button key={s.place_id} type="button" onMouseDown={() => handleSelectSuggestion(s, true)} className={suggestionBtnStyle}>{s.description}</button>))}
          </div>
        )}
      </div>

      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-1">Destination / Delivery Location <span className="text-red-500">*</span></label>
        <div className="relative">
          <input type="text" value={destInput} onChange={(e) => { const val = e.target.value; setDestInput(val); if (onDestinationChange) onDestinationChange(val); searchPlaces(val, setDestSuggestions, setShowDestSuggestions); if (!val) { setDestCoords(null); setDirections(null); } }} onKeyDown={(e) => handleInputKeyDown(e, false)} onFocus={() => destSuggestions.length > 0 && setShowDestSuggestions(true)} onBlur={() => setTimeout(() => setShowDestSuggestions(false), 200)} placeholder="Search for delivery location..." className={inputStyle} style={{ borderColor: '#E8ECEF' }} />
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          {destInput && (<button type="button" onClick={clearDest} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><FaTimes size={14} /></button>)}
        </div>
        {showDestSuggestions && destSuggestions.length > 0 && (
          <div className="absolute z-20 w-full bg-white rounded-lg shadow-lg border mt-1 max-h-48 overflow-y-auto" style={{ borderColor: '#E8ECEF' }}>
            {destSuggestions.map(s => (<button key={s.place_id} type="button" onMouseDown={() => handleSelectSuggestion(s, false)} className={suggestionBtnStyle}>{s.description}</button>))}
          </div>
        )}
      </div>

      {waypoints.map((wp, index) => (
        <div key={index} className="flex gap-2 items-center ml-4">
          <span className="text-xs font-medium text-gray-400 w-12">Stop {index + 1}</span>
          <div className="flex-1 relative">
            <input type="text" value={wp.address} onChange={(e) => updateWaypoint(index, e.target.value)} placeholder="Add stop location..." className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-green" style={{ borderColor: '#E8ECEF' }} />
          </div>
          <button type="button" onClick={() => removeWaypoint(index)} className="text-red-400 hover:text-red-600 p-1"><FaTimes size={14} /></button>
        </div>
      ))}

      <div className="flex gap-2">
        <button type="button" onClick={addWaypoint} className="flex items-center gap-1 px-3 py-2 bg-gray-100 rounded-lg text-sm hover:bg-gray-200 transition-colors"><FaPlus size={12} /> Add Stop</button>
        <div className="text-xs text-blue-600 flex items-center gap-1">
          {loading ? <FaSpinner className="animate-spin" /> : <FaRoute />}
          {loading ? 'Calculating route...' : 'Route auto-calculates when both locations are set'}
        </div>
      </div>

      {error && (<div className="text-red-500 text-sm flex items-center gap-1"><span>⚠</span> {error}</div>)}

      {distance && duration && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-xs flex gap-4">
          <span className="text-green-700">📍 <strong>{Math.round(distance)} km</strong></span>
          <span className="text-green-700">⏱ <strong>{Math.round(duration)} min</strong></span>
          <span className="text-gray-400">| Route calculated — use Add Stop to reroute via different roads</span>
        </div>
      )}

      <div className="relative border rounded-lg overflow-hidden" style={{ height: '350px' }} id="route-map-container">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={originCoords || destCoords || defaultCenter}
          zoom={originCoords || destCoords ? 7 : 5}
          onLoad={(mapInstance) => {
            setMap(mapInstance);
            // Attach click handler natively via Google Maps API (not React) to avoid event interference
            mapInstance.addListener('click', (e) => {
              if (!e || !e.latLng) return;
              const lat = e.latLng.lat(); const lng = e.latLng.lng();
              const geocoder = new window.google.maps.Geocoder();
              geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                const address = (status === 'OK' && results && results.length > 0) ? results[0].formatted_address : `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
                // Only set if no route is displayed (route drag takes priority)
                if (!originCoords) {
                  const coords = { lat, lng, address };
                  if (onOriginChange) onOriginChange(address); if (onOriginCoordsChange) onOriginCoordsChange(coords);
                } else if (!destCoords) {
                  const coords = { lat, lng, address };
                  if (onDestinationChange) onDestinationChange(address); if (onDestCoordsChange) onDestCoordsChange(coords);
                }
              });
            });
          }}
          options={{ mapTypeControl: false, streetViewControl: false, fullscreenControl: true, zoomControl: true }}
        >
          {directions ? (
            <DirectionsRenderer
              directions={directions}
              onLoad={(rendererInstance) => {
                // Forcefully apply draggable and options imperatively on load (bypasses React wrapper issues)
                rendererInstance.setOptions({
                  draggable: true,
                  polylineOptions: { 
                    strokeColor: '#1A73E8', 
                    strokeWeight: 5, 
                    strokeOpacity: 0.8 
                  },
                  suppressMarkers: false
                });

                window.google.maps.event.addListener(rendererInstance, 'directions_changed', () => {
                  const newRoute = rendererInstance.getDirections();
                  if (newRoute && newRoute.routes && newRoute.routes[0] && newRoute.routes[0].legs && newRoute.routes[0].legs[0]) {
                    const leg = newRoute.routes[0].legs[0];
                    // The user DRAGGED the route — capture the route exactly as drawn, which
                    // is the entire point of route capture.
                    if (onRouteChange) onRouteChange(buildRoutePayload(newRoute));
                    const distKm = leg.distance.value / 1000;
                    const durMin = leg.duration.value / 60;
                    setDistance(distKm);
                    setDuration(durMin);
                    if (onDistanceCalculated) {
                      onDistanceCalculated({ 
                        distance_km: Math.round(distKm * 10) / 10, 
                        duration_min: Math.round(durMin), 
                        distance_text: leg.distance.text, 
                        duration_text: leg.duration.text 
                      });
                    }
                  }
                });
              }}
            />
          ) : (
            <>
              {originCoords && (
                <Marker position={{ lat: originCoords.lat, lng: originCoords.lng }} label="A" draggable={true}
                  onDragEnd={(e) => {
                    const lat = e.latLng.lat(); const lng = e.latLng.lng();
                    const geocoder = new window.google.maps.Geocoder();
                    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                      const address = (status === 'OK' && results && results.length > 0) ? results[0].formatted_address : `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
                      const coords = { lat, lng, address }; setOriginCoords(coords); setOriginInput(address);
                      if (onOriginChange) onOriginChange(address); if (onOriginCoordsChange) onOriginCoordsChange(coords);
                    });
                  }}
                />
              )}
              {destCoords && (
                <Marker position={{ lat: destCoords.lat, lng: destCoords.lng }} label="B" draggable={true}
                  onDragEnd={(e) => {
                    const lat = e.latLng.lat(); const lng = e.latLng.lng();
                    const geocoder = new window.google.maps.Geocoder();
                    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                      const address = (status === 'OK' && results && results.length > 0) ? results[0].formatted_address : `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
                      const coords = { lat, lng, address }; setDestCoords(coords); setDestInput(address);
                      if (onDestinationChange) onDestinationChange(address); if (onDestCoordsChange) onDestCoordsChange(coords);
                    });
                  }}
                />
              )}
            </>
          )}
          {!originCoords && !destCoords && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <p className="text-sm text-gray-400 bg-white/80 px-4 py-2 rounded-lg">Search above or click the map to set locations</p>
            </div>
          )}
        </GoogleMap>
        {originCoords && (<div className="absolute bottom-2 left-2 bg-white rounded-lg shadow-md px-2 py-1 text-xs text-gray-600 border">A: {originCoords.lat.toFixed(4)}, {originCoords.lng.toFixed(4)}</div>)}
        {destCoords && (<div className="absolute bottom-2 right-2 bg-white rounded-lg shadow-md px-2 py-1 text-xs text-gray-600 border">B: {destCoords.lat.toFixed(4)}, {destCoords.lng.toFixed(4)}</div>)}
      </div>

      <p className="text-xs text-gray-400">Type an address, select from dropdown, click the map, or drag markers A/B to adjust</p>
    </div>
  );
}

export default InteractiveRouteMap;